import type { Conversation } from '../../types/profile';

interface ConversationListProps {
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  onConversationSelect: (conversation: Conversation) => void;
}

export function ConversationList({ 
  conversations, 
  selectedConversation, 
  onConversationSelect 
}: ConversationListProps) {
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-IN', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
      });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString('en-IN', { 
        weekday: 'short',
        timeZone: 'Asia/Kolkata',
      });
    } else {
      return date.toLocaleDateString('en-IN', { 
        month: 'short', 
        day: 'numeric',
        timeZone: 'Asia/Kolkata',
      });
    }
  };

  const truncateMessage = (message: string, maxLength: number = 60) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  const getConversationTitle = (conversation: Conversation) => {
    // Use the last message sender name for the title
    return conversation.lastMessage?.senderName || 'Support Team';
  };

  const getConversationSubtitle = () => {
    // This could be the company name or job title
    return `${conversations[0]?.lastMessage?.senderType === 'admin' ? 'Support' : 'Your'} Conversation`;
  };

  return (
    <div className="divide-y divide-gray-200">
      {conversations.map((conversation) => (
        <button
          key={conversation.id}
          onClick={() => onConversationSelect(conversation)}
          className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
            selectedConversation?.id === conversation.id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
          }`}
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {getConversationTitle(conversation)}
                </p>
                {conversation.lastMessage && (
                  <p className="text-xs text-gray-500 flex-shrink-0">
                    {formatTime(conversation.lastMessage.timestamp)}
                  </p>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-1">
                {getConversationSubtitle()}
              </p>
              {conversation.lastMessage && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600 truncate">
                    {conversation.lastMessage.senderType === 'user' ? 'You: ' : ''}
                    {truncateMessage(conversation.lastMessage.content)}
                  </p>
                  {conversation.unreadCount > 0 && (
                    <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-blue-600 rounded-full">
                      {conversation.unreadCount}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}